/**
 * itemsReport — o que foi VENDIDO, linha por linha, com margem.
 *
 * ─── O QUE ESTE MÓDULO SUBSTITUI ────────────────────────────────────────────
 *
 * O card "Serviços mais vendidos" agrupava por **`quotes.title`**: não lia
 * `quote_items`, não distinguia serviço de produto, contava DOCUMENTOS em vez de
 * itens, e caía num fallback "Orçamento padrão" que virava a linha mais vendida
 * de quem não preenche título. Era um proxy fraco disfarçado de relatório de
 * vendas. Aqui a agregação é a de verdade: por `product_id`, por `item_type`, e
 * por cliente.
 *
 * ─── AS QUATRO REGRAS QUE QUEBRAM EM SILÊNCIO SE IGNORADAS ──────────────────
 *
 * 1. **Ausência de custo NUNCA é zero** (armadilha #61). `Number(null) === 0`
 *    faria a margem de um item sem custo aparecer como 100% — o número mais
 *    perigoso possível, porque parece ótimo. Aqui a margem é calculada SÓ sobre
 *    a receita das linhas que têm custo ({@link ItemAggregate.costedRevenue}), e
 *    quantas linhas ficaram de fora é devolvido e mostrado na tela. Um item cujo
 *    custo nunca foi informado tem `margin === null`, não margem cheia.
 *
 * 2. **A linha de DESPESA COBRÁVEL não é venda nem item sem custo.** Ela tem
 *    `unit_cost` null de propósito (é repasse: receita e despesa ao mesmo tempo,
 *    margem zero — ver `billableExpense.ts`). Contá-la como produto vendido
 *    inventaria um "produto" chamado "Pedágio"; contá-la como item sem custo
 *    seria um alarme falso permanente. Ela sai da agregação e é reportada num
 *    balde próprio.
 *
 * 3. **Item avulso (sem `product_id`) tem balde próprio.** Descartar esconderia
 *    receita real — a maior parte do que muita gente digita não vem do catálogo.
 *    Agrupar junto com um produto de verdade misturaria coisas diferentes. Eles
 *    são agrupados pelo NOME normalizado, e marcados com `productId: null`.
 *
 * 4. **A conta de custo NÃO é reimplementada aqui.** Vem de `profitReport.ts`
 *    (`cogsItemLineCost`, `isPassthroughLine`) — a mesma que a aba Lucro usa.
 *    Duas definições de "tem custo?" fariam a aba Itens e a aba Lucro discordarem
 *    sem nenhuma das duas parecer errada.
 *
 * ─── RECEITA POR ITEM ≠ TOTAL DO DOCUMENTO ──────────────────────────────────
 *
 * A receita de uma linha é {@link quoteItemLineTotal} (a fórmula única do G3:
 * quantidade × preço − desconto DA LINHA, em centavos). O desconto de CABEÇALHO
 * e o FRETE são do documento, não da linha, e não são rateados — ratear frete
 * por item é uma escolha contábil que ninguém pediu, e ratear desconto mudaria a
 * margem de um produto conforme o desconto que o vendedor deu naquele dia. Por
 * isso a soma das receitas por item NÃO fecha com a soma de `quotes.total`, e a
 * tela diz isso ({@link ITEMS_REVENUE_NOTE}).
 */
import { roundMoney } from '@/lib/pricing';
import { quoteItemLineTotal } from '@/lib/quoteTotals';
import { cogsItemLineCost, isPassthroughLine, type CogsItem } from '@/lib/profitReport';
import type { IsoDateRange } from '@/lib/paymentsReport';

export const ITEMS_REVENUE_NOTE =
  'A receita por item é a soma das linhas. Desconto do documento e frete são do '
  + 'cabeçalho e não são rateados entre os itens, então este total não fecha com o '
  + 'total dos orçamentos.';

/** Rótulo do balde de item digitado à mão (sem vínculo com o catálogo). */
export const ADHOC_ITEM_BADGE = 'Avulso';

export type ItemKind = 'service' | 'product' | 'unknown';

export const ITEM_KIND_LABELS: Record<ItemKind, string> = {
  service: 'Serviços',
  product: 'Produtos',
  unknown: 'Sem tipo',
};

/** Uma linha de `quote_items`, no mínimo que a agregação precisa. */
export interface ItemsReportLine extends CogsItem {
  product_id?: string | null;
  name?: string | null;
  item_type?: string | null;
  unit?: string | null;
  unit_price?: number | string | null;
  discount?: number | string | null;
  discount_type?: string | null;
}

/** Documento (orçamento) normalizado — o hook converte a linha de `quotes`. */
export interface ItemsReportDocument {
  id: string;
  status: string | null;
  /** Competência 'YYYY-MM-DD' (`approved_at ?? created_at`), calendário LOCAL. */
  date: string | null;
  clientId?: string | null;
  clientName?: string | null;
  items: readonly ItemsReportLine[];
}

/**
 * Status que contam como venda — os MESMOS do KPI "Vendido" da aba Orçamentos e
 * do CMV da aba Lucro. Um orçamento pendente pode nunca acontecer.
 */
export const SOLD_QUOTE_STATUSES = ['approved', 'completed'] as const;

export interface ItemAggregate {
  /** `product_id` real, ou `avulso:<nome normalizado>` para item digitado. */
  key: string;
  /** `null` quando o item não veio do catálogo. */
  productId: string | null;
  name: string;
  kind: ItemKind;
  unit: string | null;
  /** Soma das quantidades (un, kg e h somam juntos — a tela avisa). */
  quantity: number;
  /** Σ do total de cada linha (desconto de linha aplicado). */
  revenue: number;
  /** Nº de linhas e de documentos distintos em que o item apareceu. */
  lines: number;
  documents: number;
  /** Custo somado SÓ das linhas que têm custo informado. */
  cost: number;
  /** Receita das linhas COM custo — a base honesta da margem. */
  costedRevenue: number;
  /** Linhas deste item sem custo informado. Nunca somadas como zero. */
  missingCostLines: number;
  /** `costedRevenue − cost`. `null` quando NENHUMA linha tem custo. */
  margin: number | null;
  /** Margem em % de `costedRevenue`. `null` sem custo ou sem receita costada. */
  marginPercent: number | null;
}

export interface ClientMarginAggregate {
  clientId: string | null;
  name: string;
  revenue: number;
  cost: number;
  costedRevenue: number;
  missingCostLines: number;
  margin: number | null;
  marginPercent: number | null;
  documents: number;
}

export interface KindTotals {
  kind: ItemKind;
  label: string;
  revenue: number;
  quantity: number;
  lines: number;
}

export interface ItemsReport {
  /** Itens do maior para o menor faturamento. Lista COMPLETA — quem corta é a tela. */
  items: ItemAggregate[];
  /** Serviço × produto (× sem tipo, quando existir). */
  byKind: KindTotals[];
  clients: ClientMarginAggregate[];
  /** Documentos que entraram na conta. */
  documentCount: number;
  totalRevenue: number;
  totalQuantity: number;
  totalLines: number;
  totalCost: number;
  totalCostedRevenue: number;
  /** Linhas sem custo em todo o período — o número que impede margem inflada. */
  missingCostLines: number;
  totalMargin: number | null;
  totalMarginPercent: number | null;
  /** Linhas de despesa cobrável (repasse) descartadas, e quanto elas somam. */
  passthroughLines: number;
  passthroughRevenue: number;
  isEmpty: boolean;
}

export const ITEMS_REPORT_SORT_FIELDS = [
  { value: 'revenue', label: 'Faturamento' },
  { value: 'quantity', label: 'Quantidade' },
  { value: 'margin', label: 'Margem' },
  { value: 'name', label: 'Nome' },
] as const;

export type ItemsReportSortField = (typeof ITEMS_REPORT_SORT_FIELDS)[number]['value'];
export type ItemsReportSortDirection = 'asc' | 'desc';

export interface ItemsReportSort {
  field: ItemsReportSortField;
  direction: ItemsReportSortDirection;
}

export const DEFAULT_ITEMS_REPORT_SORT: ItemsReportSort = {
  field: 'revenue',
  direction: 'desc',
};

/**
 * Classificação do agregado de itens. É necessariamente client-side: receita,
 * quantidade e margem só existem depois de somar as linhas. Quando a leitura
 * vier truncada, a UI avisa que a ordem considera apenas o conjunto carregado.
 */
export function sortItemsReport(report: ItemsReport, sort: ItemsReportSort): ItemsReport {
  const direction = sort.direction === 'asc' ? 1 : -1;
  const items = report.items
    .map((item, index) => ({ item, index }))
    .sort((left, right) => {
      const leftValue = sort.field === 'name'
        ? left.item.name
        : sort.field === 'margin'
          ? left.item.margin
          : left.item[sort.field];
      const rightValue = sort.field === 'name'
        ? right.item.name
        : sort.field === 'margin'
          ? right.item.margin
          : right.item[sort.field];
      const leftMissing = leftValue == null || leftValue === '';
      const rightMissing = rightValue == null || rightValue === '';
      if (leftMissing !== rightMissing) return leftMissing ? 1 : -1;
      let compared = 0;
      if (sort.field === 'name') {
        compared = left.item.name.localeCompare(right.item.name, 'pt-BR', { sensitivity: 'base' });
      } else {
        compared = Number(leftValue) - Number(rightValue);
      }
      return compared * direction || left.index - right.index;
    })
    .map(({ item }) => item);

  return { ...report, items };
}

function money(raw: unknown): number {
  if (raw == null || raw === '') return 0;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

function inIsoRange(date: string | null | undefined, range: IsoDateRange): boolean {
  if (!date) return false;
  if (range.from && date < range.from) return false;
  if (range.to && date > range.to) return false;
  return true;
}

function itemKind(raw: string | null | undefined): ItemKind {
  if (raw === 'service' || raw === 'product') return raw;
  return 'unknown';
}

/** Chave do balde. Produto do catálogo → id; avulso → nome normalizado. */
function bucketKey(line: ItemsReportLine): { key: string; productId: string | null } {
  const productId = line?.product_id ? String(line.product_id) : null;
  if (productId) return { key: productId, productId };
  const name = String(line?.name ?? '').trim().toLowerCase();
  return { key: `avulso:${name || 'sem-nome'}`, productId: null };
}

function marginOf(costedRevenue: number, cost: number, hasCost: boolean) {
  if (!hasCost) return { margin: null, marginPercent: null };
  const margin = roundMoney(costedRevenue - cost);
  return {
    margin,
    // Guarda de divisão por zero: sem receita costada não existe percentual
    // (≠ 0%, que significaria "vendeu no custo").
    marginPercent: costedRevenue > 0 ? (margin / costedRevenue) * 100 : null,
  };
}

/**
 * A agregação inteira.
 *
 * `documents` já vem recortado pelo período no servidor, mas o recorte final é
 * feito aqui, no calendário local, pelo mesmo motivo do CMV: a competência é
 * `approved_at ?? created_at` e comparar em UTC jogaria um documento aprovado às
 * 21h30 do dia 31 para o mês seguinte.
 */
export function computeItemsReport(
  documents: readonly ItemsReportDocument[],
  range: IsoDateRange,
  statuses: readonly string[] = SOLD_QUOTE_STATUSES,
): ItemsReport {
  const buckets = new Map<string, ItemAggregate & { _docs: Set<string> }>();
  const clientBuckets = new Map<
    string,
    ClientMarginAggregate & { _docs: Set<string>; _hasCost: boolean }
  >();

  let documentCount = 0;
  let totalRevenue = 0;
  let totalQuantity = 0;
  let totalLines = 0;
  let totalCost = 0;
  let totalCostedRevenue = 0;
  let missingCostLines = 0;
  let passthroughLines = 0;
  let passthroughRevenue = 0;
  const hasCostByKey = new Set<string>();

  for (const doc of documents ?? []) {
    if (!statuses.includes(String(doc?.status ?? ''))) continue;
    if (!inIsoRange(doc?.date, range)) continue;

    documentCount += 1;

    const clientKey = doc.clientId ? String(doc.clientId) : '__none__';
    const clientBucket = clientBuckets.get(clientKey) ?? {
      clientId: doc.clientId ?? null,
      name: String(doc.clientName ?? '').trim() || 'Sem cliente',
      revenue: 0,
      cost: 0,
      costedRevenue: 0,
      missingCostLines: 0,
      margin: null,
      marginPercent: null,
      documents: 0,
      _docs: new Set<string>(),
      _hasCost: false,
    };
    clientBucket._docs.add(String(doc.id));
    clientBuckets.set(clientKey, clientBucket);

    for (const line of doc.items ?? []) {
      const revenue = quoteItemLineTotal(line);

      // Repasse: nem venda, nem item sem custo. Sai da conta e é reportado.
      if (isPassthroughLine(line)) {
        passthroughLines += 1;
        passthroughRevenue = roundMoney(passthroughRevenue + revenue);
        continue;
      }

      const { key, productId } = bucketKey(line);
      const bucket = buckets.get(key) ?? {
        key,
        productId,
        name: String(line?.name ?? '').trim() || 'Sem nome',
        kind: itemKind(line?.item_type),
        unit: line?.unit ? String(line.unit) : null,
        quantity: 0,
        revenue: 0,
        lines: 0,
        documents: 0,
        cost: 0,
        costedRevenue: 0,
        missingCostLines: 0,
        margin: null,
        marginPercent: null,
        _docs: new Set<string>(),
      };

      const quantity = money(line?.quantity);
      bucket.quantity += quantity;
      bucket.revenue = roundMoney(bucket.revenue + revenue);
      bucket.lines += 1;
      bucket._docs.add(String(doc.id));

      totalQuantity += quantity;
      totalRevenue = roundMoney(totalRevenue + revenue);
      totalLines += 1;

      const lineCost = cogsItemLineCost(line);
      if (lineCost === null) {
        bucket.missingCostLines += 1;
        clientBucket.missingCostLines += 1;
        missingCostLines += 1;
      } else {
        bucket.cost = roundMoney(bucket.cost + lineCost);
        bucket.costedRevenue = roundMoney(bucket.costedRevenue + revenue);
        hasCostByKey.add(key);
        clientBucket.cost = roundMoney(clientBucket.cost + lineCost);
        clientBucket.costedRevenue = roundMoney(clientBucket.costedRevenue + revenue);
        clientBucket._hasCost = true;
        totalCost = roundMoney(totalCost + lineCost);
        totalCostedRevenue = roundMoney(totalCostedRevenue + revenue);
      }

      clientBucket.revenue = roundMoney(clientBucket.revenue + revenue);
      buckets.set(key, bucket);
    }
  }

  const items: ItemAggregate[] = Array.from(buckets.values())
    .map(({ _docs, ...rest }) => {
      const { margin, marginPercent } = marginOf(
        rest.costedRevenue,
        rest.cost,
        hasCostByKey.has(rest.key),
      );
      return { ...rest, documents: _docs.size, margin, marginPercent };
    })
    .sort((a, b) => b.revenue - a.revenue || a.name.localeCompare(b.name, 'pt-BR'));

  const clients: ClientMarginAggregate[] = Array.from(clientBuckets.values())
    .map(({ _docs, _hasCost, ...rest }) => {
      const { margin, marginPercent } = marginOf(rest.costedRevenue, rest.cost, _hasCost);
      return { ...rest, documents: _docs.size, margin, marginPercent };
    })
    .sort((a, b) => b.revenue - a.revenue || a.name.localeCompare(b.name, 'pt-BR'));

  // Serviço × produto na ordem canônica; "Sem tipo" só aparece se existir, para
  // não poluir a tela de quem sempre classifica os itens.
  const byKind: KindTotals[] = (['service', 'product', 'unknown'] as ItemKind[])
    .map((kind) => {
      const of = items.filter((i) => i.kind === kind);
      return {
        kind,
        label: ITEM_KIND_LABELS[kind],
        revenue: roundMoney(of.reduce((sum, i) => sum + i.revenue, 0)),
        quantity: of.reduce((sum, i) => sum + i.quantity, 0),
        lines: of.reduce((sum, i) => sum + i.lines, 0),
      };
    })
    .filter((t) => t.lines > 0);

  const { margin: totalMargin, marginPercent: totalMarginPercent } = marginOf(
    totalCostedRevenue,
    totalCost,
    hasCostByKey.size > 0,
  );

  return {
    items,
    byKind,
    clients,
    documentCount,
    totalRevenue,
    totalQuantity,
    totalLines,
    totalCost,
    totalCostedRevenue,
    missingCostLines,
    totalMargin,
    totalMarginPercent,
    passthroughLines,
    passthroughRevenue,
    isEmpty: documentCount === 0,
  };
}

/**
 * Linha de `quotes` (com `quote_items` embutidos) → documento do relatório.
 *
 * A lista de campos lidos fica num lugar só, pelo mesmo motivo de
 * `toCogsDocumentFromQuote`: perder `unit_cost` aqui não dá erro nenhum, só faz
 * TODA a margem sumir; perder `finance_entry_id` traz o repasse de volta para
 * dentro do relatório como se fosse produto.
 */
export function toItemsDocument(
  row: any,
  competenceDate: string | null,
): ItemsReportDocument {
  return {
    id: String(row?.id ?? ''),
    status: row?.status ?? null,
    date: competenceDate,
    clientId: row?.client_id ?? null,
    clientName: row?.client_name ?? null,
    items: (row?.quote_items ?? []).map((item: any) => ({
      product_id: item?.product_id ?? null,
      name: item?.name ?? null,
      item_type: item?.item_type ?? null,
      unit: item?.unit ?? null,
      quantity: item?.quantity,
      unit_price: item?.unit_price,
      unit_cost: item?.unit_cost,
      discount: item?.discount,
      discount_type: item?.discount_type,
      finance_entry_id: item?.finance_entry_id ?? null,
    })),
  };
}
